/**
 * The write outbox.
 *
 * The user rates faster than the network, so no keystroke waits for a request. Every rating is
 * appended here, painted immediately, and flushed by a single serial worker in the background.
 *
 * The requirement this exists for: **losing a sitting of 200 ratings to a dropped connection is
 * the one failure this app cannot have.** So the queue is durable across reloads, retries with
 * backoff, flushes on `online`, and never blocks the interface. Each entry carries a
 * client-generated id, and the server is idempotent on it, so a retry the server already applied
 * is a no-op rather than a duplicate history row.
 */

export type OutboxKind = "rate" | "unrate" | "skip" | "favourite";

export interface OutboxEntry {
  clientEventId: string;
  kind: OutboxKind;
  songId: number;
  value?: number;
  /** `favourite` only: the resolved on/off state, never a toggle — see defaultSend below. */
  favourite?: boolean;
  ratedAt: string;
  attempts: number;
}

/**
 * The write an undo produces.
 *
 * `Z` on a song rated for the FIRST time is a retraction, and `unrate` is right. `Z` on a song
 * that already had a rating is a correction back to what it was, and sending `unrate` there
 * deletes a judgement the user made months ago — the interface shows the old value restored while
 * the server holds nothing, and the two never reconcile because the clear is written to the
 * append-only log as a real event.
 *
 * `RateMachine.undo` already knows which of the two happened; it hands the restored value to
 * `onUndone`. This is that value being used. It lives here rather than inline at the call site
 * because it is a decision about what to write, and decisions about what to write are the thing
 * this module is for.
 */
export function undoEntry(
  songId: number,
  restored: number | null,
  clientEventId: string,
  ratedAt: string,
): Omit<OutboxEntry, "attempts"> {
  return restored === null
    ? { clientEventId, kind: "unrate", songId, ratedAt }
    : { clientEventId, kind: "rate", songId, value: restored, ratedAt };
}

/** Injectable so the queue is testable without a browser. */
export interface OutboxStore {
  read(): OutboxEntry[];
  write(entries: OutboxEntry[]): void;
}

export function localStorageStore(key = "trackmark.outbox"): OutboxStore {
  return {
    read() {
      try {
        const raw = globalThis.localStorage?.getItem(key);
        return raw ? (JSON.parse(raw) as OutboxEntry[]) : [];
      } catch {
        // A corrupt queue is worse than an empty one only if it blocks writes; it must not.
        return [];
      }
    },
    write(entries) {
      try {
        globalThis.localStorage?.setItem(key, JSON.stringify(entries));
      } catch {
        // Quota or private-mode failure. The in-memory queue still flushes this session.
      }
    },
  };
}

export function memoryStore(initial: OutboxEntry[] = []): OutboxStore {
  let entries = [...initial];
  return {
    read: () => [...entries],
    write: (next) => {
      entries = [...next];
    },
  };
}

export interface OutboxOptions {
  store?: OutboxStore;
  /** Injected for tests; defaults to global fetch. */
  send?: (entry: OutboxEntry) => Promise<void>;
  /** Called whenever the pending count or error state changes. */
  onChange?: (state: OutboxState) => void;
  /** Backoff schedule in ms. The last value repeats. */
  backoff?: number[];
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

export interface OutboxState {
  pending: number;
  /** True once a flush has failed and the queue is waiting to retry. */
  failing: boolean;
  /**
   * True when the retry is waiting on a login rather than on the network. The distinction is
   * worth a state field because the two need opposite things from the user: one needs them to
   * carry on, and one needs them to stop and sign in — and telling somebody their ratings will
   * sync when the connection returns, when the connection is fine, leaves them rating another
   * two hundred songs into a queue that cannot drain.
   */
  needsAuth: boolean;
  /**
   * Writes the server refused outright, and which are therefore lost.
   *
   * Counted rather than hidden. A dropped entry that looks exactly like a delivered one is how a
   * rating disappears without anybody finding out, which is the one failure this queue exists to
   * prevent.
   */
  dropped: number;
}

/**
 * A write the server will not accept however many times it is offered — a malformed body, a song
 * that no longer exists. Distinct from a failure worth retrying, because the queue's response to
 * the two is opposite: hold on to one, let go of the other.
 */
export class UnsendableError extends Error {
  constructor(readonly status: number) {
    super(`the server refused this write: ${status}`);
    this.name = "UnsendableError";
  }
}

/** A failure worth offering again, carrying the status so the queue can say WHY it is waiting. */
export class RetryableError extends Error {
  constructor(readonly status: number) {
    super(`send failed: ${status}`);
    this.name = "RetryableError";
  }
}

/**
 * Whether a status is worth offering again.
 *
 * 401 is the one that matters and the one that was wrong. The session cookie lasts ninety days;
 * when it lapses every write answers 401, and treating that as final emptied the durable queue of
 * a whole sitting while the strip painted each rating as saved. It is recoverable — the user logs
 * back in and the queue drains — so it is retryable.
 *
 * 408 and 429 are the same shape: the server is saying not now rather than never.
 */
export function isRetryable(status: number): boolean {
  return status === 401 || status === 408 || status === 429 || status >= 500;
}

const DEFAULT_BACKOFF = [1_000, 2_000, 5_000, 15_000, 30_000];

export class Outbox {
  private store: OutboxStore;
  private queue: OutboxEntry[];
  /** The in-flight drain, so concurrent callers await the same one rather than returning early. */
  private flushing: Promise<void> | null = null;
  private timer: unknown = null;
  private failing = false;
  private needsAuth = false;
  private dropped = 0;

  private readonly send: (entry: OutboxEntry) => Promise<void>;
  private readonly onChange: ((state: OutboxState) => void) | undefined;
  private readonly backoff: number[];
  private readonly setT: (fn: () => void, ms: number) => unknown;
  private readonly clearT: (handle: unknown) => void;

  constructor(options: OutboxOptions = {}) {
    this.store = options.store ?? localStorageStore();
    this.queue = this.store.read();
    this.send = options.send ?? defaultSend;
    this.onChange = options.onChange;
    this.backoff = options.backoff ?? DEFAULT_BACKOFF;
    this.setT = options.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
    this.clearT = options.clearTimeout ?? ((h) => globalThis.clearTimeout(h as number));
  }

  get state(): OutboxState {
    return {
      pending: this.queue.length,
      failing: this.failing,
      needsAuth: this.needsAuth,
      dropped: this.dropped,
    };
  }

  /** Queue a write. Returns immediately — nothing here is ever awaited by the input path. */
  enqueue(entry: Omit<OutboxEntry, "attempts">): void {
    // A newer rating for the same song supersedes an unsent older one: the user changed their
    // mind before the network caught up, and only the final judgement is worth a history row.
    if (entry.kind === "rate" || entry.kind === "unrate") {
      this.queue = this.queue.filter(
        (e) => !(e.songId === entry.songId && (e.kind === "rate" || e.kind === "unrate")),
      );
    }

    // Same idea for F, on its own axis: pressing it four times offline is one final state, not
    // four requests. Collapsing here is what makes the explicit `favourite` value below safe.
    if (entry.kind === "favourite") {
      this.queue = this.queue.filter((e) => !(e.songId === entry.songId && e.kind === "favourite"));
    }
    this.queue.push({ ...entry, attempts: 0 });
    this.persist();
    void this.flush();
  }

  /**
   * Drains the queue in order. Serial on purpose: history should record what happened, in the
   * order it happened. Awaiting this always means "drained or stalled" — a call made while a
   * drain is already running joins that drain rather than returning early, which would otherwise
   * let `await flush()` resolve with entries still pending.
   */
  flush(): Promise<void> {
    if (this.flushing) return this.flushing;
    if (this.queue.length === 0) return Promise.resolve();

    this.flushing = this.drain().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async drain(): Promise<void> {
    while (this.queue.length > 0) {
      const entry = this.queue[0]!;
      try {
        await this.send(entry);
        this.queue.shift();
        this.failing = false;
        this.needsAuth = false;
        this.persist();
      } catch (error) {
        if (error instanceof UnsendableError) {
          /*
           * The server will never accept this one, so holding it would block every write behind
           * it forever. It is dropped — and COUNTED, because a lost write that looks exactly like
           * a delivered one is the failure this queue exists to prevent. The interface says so.
           */
          this.queue.shift();
          this.dropped += 1;
          this.persist();
          continue;
        }

        entry.attempts += 1;
        this.failing = true;
        this.needsAuth = error instanceof RetryableError && error.status === 401;
        this.persist();
        this.scheduleRetry(entry.attempts);
        return;
      }
    }
  }

  private scheduleRetry(attempts: number): void {
    if (this.timer !== null) return;
    const wait = this.backoff[Math.min(attempts - 1, this.backoff.length - 1)]!;
    this.timer = this.setT(() => {
      this.timer = null;
      void this.flush();
    }, wait);
  }

  /** Call on `online`. Cancels any pending backoff and tries immediately. */
  resume(): void {
    if (this.timer !== null) {
      this.clearT(this.timer);
      this.timer = null;
    }
    void this.flush();
  }

  dispose(): void {
    if (this.timer !== null) this.clearT(this.timer);
    this.timer = null;
  }

  private persist(): void {
    this.store.write(this.queue);
    this.onChange?.(this.state);
  }
}

const PATHS: Record<OutboxKind, string> = {
  rate: "rating",
  unrate: "rating",
  skip: "skip",
  favourite: "favourite",
};

const METHODS: Record<OutboxKind, string> = {
  rate: "PUT",
  unrate: "DELETE",
  skip: "POST",
  favourite: "POST",
};

async function defaultSend(entry: OutboxEntry): Promise<void> {
  const response = await fetch(`/api/songs/${entry.songId}/${PATHS[entry.kind]}`, {
    method: METHODS[entry.kind],
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      value: entry.value,
      // The RESOLVED state, not a toggle. A retried toggle would flip the song back, and the
      // outbox retries by design — so the client decides and the server obeys.
      ...(entry.kind === "favourite" ? { value: undefined, favourite: entry.favourite } : {}),
      clientEventId: entry.clientEventId,
      ratedAt: entry.ratedAt,
    }),
  });

  if (response.ok) return;
  // The queue's two failure modes, and they are not the same thing. Anything recoverable stays
  // queued and is retried; only a write the server will never accept is let go of, and the
  // interface is told when that happens. Treating every 4xx as final is what emptied a sitting
  // into nothing the day a session expired.
  if (isRetryable(response.status)) throw new RetryableError(response.status);
  throw new UnsendableError(response.status);
}

/** The exact copy the interface uses when the queue is behind. It does not block; it says keep going. */
export const OUTBOX_ERROR =
  "THE LAST RATING DIDN'T SAVE. IT IS QUEUED AND WILL SYNC WHEN THE CONNECTION RETURNS — KEEP GOING.";

/**
 * The same situation with a different remedy: the queue is intact, but nothing will drain until
 * somebody signs in. Telling the user to keep going here would be telling them to keep filling a
 * queue that cannot empty.
 */
export const OUTBOX_AUTH_ERROR =
  "YOUR SESSION HAS EXPIRED. YOUR RATINGS ARE SAFE IN THE QUEUE — SIGN IN AGAIN AND THEY WILL SYNC.";

/** Said once, and plainly, because something was actually lost. */
export const outboxDroppedMessage = (n: number) =>
  `${n} ${n === 1 ? "RATING WAS" : "RATINGS WERE"} REFUSED BY THE SERVER AND COULD NOT BE SAVED.`;
